import { v4 as uuidv4 } from 'uuid';
import TemplateConfigurationModel from '../models/templating/api/TemplateConfigurationModel';
import TemplateFormFieldModel from '../models/templating/api/TemplateFormFieldModel';
import TemplateFormFieldOptionModel from '../models/templating/api/TemplateFormFieldOptionModel';
import TemplateFormSectionModel from '../models/templating/api/TemplateFormSectionModel';
import { ImportedTemplateFormCsvSection } from './templateFormCsv';

export interface CsvMergeResult {
    templateConfiguration: TemplateConfigurationModel;
    deletedFields: TemplateFormFieldModel[];
    errors: string[];
}

const cloneSection = (section: TemplateFormSectionModel): TemplateFormSectionModel => ({
    ...section,
    templateFormFields: [...(section.templateFormFields ?? [])],
});

function normalizeFieldOptions(
    options: TemplateFormFieldOptionModel[] | undefined,
    templateFormFieldId: string | undefined,
    regenerateOptionIds: boolean,
): TemplateFormFieldOptionModel[] {
    return (options ?? []).map(option => ({
        ...option,
        id: regenerateOptionIds || !option.id ? uuidv4() : option.id,
        templateFormFieldId,
    }));
}

const cloneField = (field: TemplateFormFieldModel): TemplateFormFieldModel => ({
    ...field,
    options: normalizeFieldOptions(field.options, field.id, false),
});

const getSectionIdentity = (section: TemplateFormSectionModel): string => section.id ?? section.title ?? '';

export const mergeCsvTemplateConfiguration = (
    existingTemplate: TemplateConfigurationModel,
    importedSections: ImportedTemplateFormCsvSection[],
): CsvMergeResult => {
    try {
        const templateConfig: TemplateConfigurationModel = {
            ...existingTemplate,
        };

        templateConfig.templateForm = templateConfig.templateForm
            ? { ...templateConfig.templateForm }
            : {
                templateFormSections: [],
                instructionsMarkdown: ''
            };

        const existingSections: TemplateFormSectionModel[] = templateConfig.templateForm.templateFormSections ?? [];
        const resolvedTemplateFormId = templateConfig.templateForm.id;

        if (!resolvedTemplateFormId) {
            return {
                templateConfiguration: existingTemplate,
                deletedFields: [],
                errors: ['Template form is missing an id. Cannot safely build an update payload from CSV.']
            };
        }

        const existingSectionById = new Map<string, TemplateFormSectionModel>();
        const existingSectionByTitle = new Map<string, TemplateFormSectionModel>();
        const existingFieldById = new Map<string, TemplateFormFieldModel>();
        const existingFieldByVariable = new Map<string, TemplateFormFieldModel>();

        existingSections.forEach(section => {
            if (section.id) existingSectionById.set(section.id, section);
            if (section.title) existingSectionByTitle.set(section.title, section);
            (section.templateFormFields ?? []).forEach(field => {
                if (field.id) existingFieldById.set(field.id, field);
                if (field.associatedVariable) existingFieldByVariable.set(field.associatedVariable, field);
            });
        });

        const claimedFieldIds = new Set<string>();
        const claimedExistingFieldIds = new Set<string>();
        const mergedSections: TemplateFormSectionModel[] = [];
        const errors: string[] = [];

        importedSections.forEach((importedSection, sectionIndex) => {
            try {
                if (!importedSection.title) {
                    errors.push(`Section ${sectionIndex + 1}: Missing section title. Please ensure the "sectionTitle" column has a value for this section before saving.`);
                    return;
                }

                const existingById = importedSection.sectionId ? existingSectionById.get(importedSection.sectionId) : undefined;
                const existingByTitle = existingById ? undefined : existingSectionByTitle.get(importedSection.title);
                const existingSection = existingById ?? existingByTitle;

                const resolvedSection: TemplateFormSectionModel = existingSection
                    ? {
                        ...cloneSection(existingSection),
                        title: importedSection.title,
                        priority: importedSection.priority ?? sectionIndex,
                        templateFormId: existingSection.templateFormId ?? resolvedTemplateFormId,
                        templateFormFields: []
                    }
                    : {
                        id: uuidv4(),
                        templateFormId: resolvedTemplateFormId,
                        title: importedSection.title,
                        priority: importedSection.priority ?? sectionIndex,
                        instructionsMarkdown: '',
                        templateFormFields: []
                    };

                if (importedSection.fields && Array.isArray(importedSection.fields)) {
                    importedSection.fields.forEach((field, index) => {
                        try {
                            if (!field.title) {
                                errors.push(`Field ${index + 1} in section "${importedSection.title}": Missing field title. Please ensure the "fieldTitle" column has a value before saving.`);
                                return;
                            }

                            let existingField: TemplateFormFieldModel | undefined;
                            if (field.fieldId) {
                                existingField = existingFieldById.get(field.fieldId);
                            }
                            if (!existingField && field.associatedVariable) {
                                existingField = existingFieldByVariable.get(field.associatedVariable);
                            }

                            if (existingField && claimedExistingFieldIds.has(existingField.id)) {
                                errors.push(`Field "${field.title}" in section "${importedSection.title}" matches an existing field that was already used by another row. Please make the CSV rows unique by fieldId or associatedVariable before saving.`);
                                return;
                            }

                            let mergedField: TemplateFormFieldModel;
                            if (existingField) {
                                claimedExistingFieldIds.add(existingField.id);
                                mergedField = {
                                    ...existingField,
                                    templateFormSectionId: resolvedSection.id,
                                    title: field.title,
                                    label: field.label ?? existingField.label,
                                    fieldType: field.fieldType || existingField.fieldType || 'text',
                                    placeholder: field.placeholder ?? existingField.placeholder ?? '',
                                    tip: field.tip ?? existingField.tip ?? '',
                                    defaultValue: field.defaultValue ?? existingField.defaultValue ?? '',
                                    conditionalDefaults: field.conditionalDefaults ?? existingField.conditionalDefaults,
                                    isRequired: field.isRequired !== undefined ? field.isRequired : existingField.isRequired,
                                    priority: field.priority !== undefined ? field.priority : (index ?? existingField.priority)
                                };
                                mergedField.options = normalizeFieldOptions(field.options ?? existingField.options, mergedField.id, false);
                                claimedFieldIds.add(existingField.id);
                            } else {
                                const newFieldId = uuidv4();
                                mergedField = {
                                    id: newFieldId,
                                    templateFormSectionId: resolvedSection.id,
                                    title: field.title,
                                    label: field.label,
                                    fieldType: field.fieldType || 'text',
                                    associatedVariable: field.associatedVariable || '',
                                    placeholder: field.placeholder || '',
                                    tip: field.tip || '',
                                    defaultValue: field.defaultValue || '',
                                    conditionalDefaults: field.conditionalDefaults,
                                    isRequired: field.isRequired || false,
                                    priority: field.priority !== undefined ? field.priority : index
                                };
                                mergedField.options = normalizeFieldOptions(field.options, newFieldId, true);
                            }

                            resolvedSection.templateFormFields.push(mergedField);
                        } catch (fieldError) {
                            errors.push(`Error processing field "${field.title || `#${index + 1}`}" in section "${importedSection.title}": ${fieldError.message}. Please check the field data before saving.`);
                        }
                    });
                }

                mergedSections.push(resolvedSection);
            } catch (sectionError) {
                errors.push(`Error processing section "${importedSection.title || `#${sectionIndex + 1}`}": ${sectionError.message}. Please check the section data before saving.`);
            }
        });

        const orphansBySectionId = new Map<string, TemplateFormFieldModel[]>();

        existingSections.forEach(section => {
            const sectionKey = getSectionIdentity(section);
            if (!sectionKey) return;
            (section.templateFormFields ?? []).forEach(field => {
                if (!claimedFieldIds.has(field.id)) {
                    if (!orphansBySectionId.has(sectionKey)) {
                        orphansBySectionId.set(sectionKey, []);
                    }
                    orphansBySectionId.get(sectionKey).push(field);
                }
            });
        });

        existingSections.forEach(existingSection => {
            const sectionKey = getSectionIdentity(existingSection);
            if (!sectionKey) return;
            const orphans = orphansBySectionId.get(sectionKey);
            if (!orphans || orphans.length === 0) return;

            const alreadyMerged = existingSection.id
                ? mergedSections.find(s => s.id === existingSection.id)
                : (existingSection.title ? mergedSections.find(s => s.title === existingSection.title) : undefined);
            if (alreadyMerged) {
                orphans.forEach(f => {
                    alreadyMerged.templateFormFields?.push({
                        ...cloneField(f),
                        templateFormSectionId: alreadyMerged.id,
                    });
                });
            } else {
                mergedSections.push({
                    ...existingSection,
                    templateFormId: existingSection.templateFormId ?? resolvedTemplateFormId,
                    templateFormFields: orphans.map(field => ({
                        ...cloneField(field),
                        templateFormSectionId: existingSection.id,
                    }))
                });
            }
        });

        const payloadErrors: string[] = [];
        const seenSectionIds = new Set<string>();
        const seenFieldIds = new Set<string>();
        const seenOptionIds = new Set<string>();
        mergedSections.forEach((section, sectionIndex) => {
            if (!section.id) {
                payloadErrors.push(`Merged section ${sectionIndex + 1} is missing an id.`);
            } else if (seenSectionIds.has(section.id)) {
                payloadErrors.push(`Duplicate merged section id detected: ${section.id}.`);
            } else {
                seenSectionIds.add(section.id);
            }
            if (!section.templateFormId) {
                payloadErrors.push(`Merged section "${section.title ?? sectionIndex + 1}" is missing templateFormId.`);
            }
            (section.templateFormFields ?? []).forEach((field, fieldIndex) => {
                if (!field.id) {
                    payloadErrors.push(`Merged field ${fieldIndex + 1} in section "${section.title ?? sectionIndex + 1}" is missing an id.`);
                } else if (seenFieldIds.has(field.id)) {
                    payloadErrors.push(`Duplicate merged field id detected: ${field.id}.`);
                } else {
                    seenFieldIds.add(field.id);
                }
                if (!field.templateFormSectionId) {
                    payloadErrors.push(`Merged field "${field.title ?? fieldIndex + 1}" in section "${section.title ?? sectionIndex + 1}" is missing templateFormSectionId.`);
                }
                (field.options ?? []).forEach(option => {
                    if (!option.id) {
                        payloadErrors.push(`Merged option "${option.label ?? 'unknown'}" in field "${field.title ?? fieldIndex + 1}" is missing an id.`);
                    } else if (seenOptionIds.has(option.id)) {
                        payloadErrors.push(`Duplicate merged option id detected: ${option.id}.`);
                    } else {
                        seenOptionIds.add(option.id);
                    }

                    if (option.templateFormFieldId && field.id && option.templateFormFieldId !== field.id) {
                        payloadErrors.push(`Merged option "${option.label ?? 'unknown'}" in field "${field.title ?? fieldIndex + 1}" has templateFormFieldId ${option.templateFormFieldId} but expected ${field.id}.`);
                    }
                });
            });
        });

        if (payloadErrors.length > 0) {
            return {
                templateConfiguration: existingTemplate,
                deletedFields: [],
                errors: payloadErrors,
            };
        }

        const normalizedSections = mergedSections.map((section, index) => {
            const sectionId = section.id ?? uuidv4();
            const normalizedFields = (section.templateFormFields ?? []).map(field => {
                const fieldId = field.id ?? uuidv4();
                return {
                    ...cloneField(field),
                    id: fieldId,
                    templateFormSectionId: sectionId,
                    options: normalizeFieldOptions(field.options, fieldId, false),
                };
            });

            return {
                ...section,
                id: sectionId,
                templateFormId: section.templateFormId ?? resolvedTemplateFormId,
                priority: section.priority ?? index,
                templateFormFields: normalizedFields,
            };
        });

        templateConfig.templateForm.templateFormSections = normalizedSections;

        const mergedFieldIds = new Set<string>();
        normalizedSections.forEach(section => {
            (section.templateFormFields ?? []).forEach(field => mergedFieldIds.add(field.id));
        });

        const deletedFields: TemplateFormFieldModel[] = [];
        existingSections.forEach(section => {
            (section.templateFormFields ?? []).forEach(field => {
                if (!mergedFieldIds.has(field.id)) {
                    deletedFields.push(field);
                }
            });
        });

        return {
            templateConfiguration: templateConfig,
            deletedFields,
            errors,
        };
    } catch (error) {
        return {
            templateConfiguration: existingTemplate,
            deletedFields: [],
            errors: [`Failed to merge template sections: ${error.message}. Please check your data and try again, or refresh the page to start over.`],
        };
    }
};
